// Sorting algorithm implementations written as generator functions.
//
// Each generator takes an array, operates on an internal copy, and yields
// discrete "operations" that the UI/hook applies to its own array copy so the
// sort can be animated step by step at a configurable speed.
//
// Operation shapes:
//   { type: 'compare',    indices: [i, j] }  -> highlight two bars
//   { type: 'swap',       indices: [i, j] }  -> swap two bars
//   { type: 'overwrite',  index, value }     -> set a bar to a value (merge sort)
//   { type: 'sorted',     index }            -> mark a bar as being in final position
//   { type: 'done' }                         -> sorting finished
//
// Every non-done operation also carries a `line` number that points at the
// matching source line in the CODE constant below, so the UI can render a
// debugger-style code viewer while the sort is running.

export const BUBBLE_CODE = `function bubbleSort(arr) {
  const n = arr.length

  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      if (arr[j] > arr[j + 1]) {
        swap(arr, j, j + 1)
      }
    }
    // arr[n - i - 1] is now in its final position
  }
  // arr[0] is now in its final position
}`

export const SELECTION_CODE = `function selectionSort(arr) {
  const n = arr.length

  for (let i = 0; i < n - 1; i++) {
    let minIdx = i
    for (let j = i + 1; j < n; j++) {
      if (arr[j] < arr[minIdx]) {
        minIdx = j
      }
    }
    if (minIdx !== i) {
      swap(arr, i, minIdx)
    }
    // arr[i] is now in its final position
  }
  // arr[n - 1] is now in its final position
}`

export const INSERTION_CODE = `function insertionSort(arr) {
  const n = arr.length

  for (let i = 1; i < n; i++) {
    let j = i
    while (j > 0 && arr[j - 1] > arr[j]) {
      swap(arr, j - 1, j)
      j--
    }
  }

  // array is now fully sorted
}`

export const MERGE_CODE = `function mergeSort(arr) {
  const merge = (l, m, r) => {
    const left = arr.slice(l, m)
    const right = arr.slice(m, r)
    let i = 0, j = 0, k = l

    while (i < left.length && j < right.length) {
      if (left[i] <= right[j]) {
        arr[k] = left[i]
        i++
      } else {
        arr[k] = right[j]
        j++
      }
      k++
    }

    while (i < left.length) {
      arr[k++] = left[i++]
    }

    while (j < right.length) {
      arr[k++] = right[j++]
    }
  }

  const sort = (l, r) => {
    if (r - l <= 1) return
    const m = Math.floor((l + r) / 2)
    sort(l, m)
    sort(m, r)
    merge(l, m, r)
  }

  sort(0, arr.length)

  // array is now fully sorted
}`

export const QUICK_CODE = `function quickSort(arr) {
  const partition = (l, r) => {
    const pivot = arr[r]
    let storeIdx = l

    for (let i = l; i < r; i++) {
      if (arr[i] < pivot) {
        if (i !== storeIdx) {
          swap(arr, i, storeIdx)
        }
        storeIdx++
      }
    }

    if (storeIdx !== r) {
      swap(arr, storeIdx, r)
    }
    return storeIdx
  }

  const sort = (l, r) => {
    if (l >= r) return
    const p = partition(l, r)
    sort(l, p - 1)
    sort(p + 1, r)
  }

  sort(0, arr.length - 1)

  // array is now fully sorted
}`

function* bubbleSort(arr) {
  const a = arr.slice()
  const n = a.length

  for (let i = 0; i < n - 1; i++) {
    for (let j = 0; j < n - i - 1; j++) {
      yield { type: 'compare', indices: [j, j + 1], line: 6 }
      if (a[j] > a[j + 1]) {
        ;[a[j], a[j + 1]] = [a[j + 1], a[j]]
        yield { type: 'swap', indices: [j, j + 1], line: 7 }
      }
    }
    yield { type: 'sorted', index: n - i - 1, line: 10 }
  }
  yield { type: 'sorted', index: 0, line: 12 }
  yield { type: 'done' }
}

function* selectionSort(arr) {
  const a = arr.slice()
  const n = a.length

  for (let i = 0; i < n - 1; i++) {
    let minIdx = i
    for (let j = i + 1; j < n; j++) {
      yield { type: 'compare', indices: [minIdx, j], line: 7 }
      if (a[j] < a[minIdx]) {
        minIdx = j
      }
    }
    if (minIdx !== i) {
      ;[a[i], a[minIdx]] = [a[minIdx], a[i]]
      yield { type: 'swap', indices: [i, minIdx], line: 12 }
    }
    yield { type: 'sorted', index: i, line: 14 }
  }
  yield { type: 'sorted', index: n - 1, line: 16 }
  yield { type: 'done' }
}

function* insertionSort(arr) {
  const a = arr.slice()
  const n = a.length

  for (let i = 1; i < n; i++) {
    let j = i
    while (j > 0) {
      yield { type: 'compare', indices: [j - 1, j], line: 6 }
      if (a[j - 1] > a[j]) {
        ;[a[j - 1], a[j]] = [a[j], a[j - 1]]
        yield { type: 'swap', indices: [j - 1, j], line: 7 }
        j--
      } else {
        break
      }
    }
  }

  for (let i = 0; i < n; i++) {
    yield { type: 'sorted', index: i, line: 12 }
  }
  yield { type: 'done' }
}

function* mergeSort(arr) {
  const a = arr.slice()
  const n = a.length

  function* merge(l, m, r) {
    const left = a.slice(l, m)
    const right = a.slice(m, r)
    let i = 0
    let j = 0
    let k = l

    while (i < left.length && j < right.length) {
      yield { type: 'compare', indices: [l + i, m + j], line: 8 }
      if (left[i] <= right[j]) {
        a[k] = left[i]
        yield { type: 'overwrite', index: k, value: left[i], line: 9 }
        i++
      } else {
        a[k] = right[j]
        yield { type: 'overwrite', index: k, value: right[j], line: 12 }
        j++
      }
      k++
    }

    while (i < left.length) {
      a[k] = left[i]
      yield { type: 'overwrite', index: k, value: left[i], line: 19 }
      i++
      k++
    }

    while (j < right.length) {
      a[k] = right[j]
      yield { type: 'overwrite', index: k, value: right[j], line: 23 }
      j++
      k++
    }
  }

  function* mergeSortRange(l, r) {
    if (r - l <= 1) return
    const m = Math.floor((l + r) / 2)
    yield* mergeSortRange(l, m)
    yield* mergeSortRange(m, r)
    yield* merge(l, m, r)
  }

  yield* mergeSortRange(0, n)
  for (let i = 0; i < n; i++) {
    yield { type: 'sorted', index: i, line: 37 }
  }
  yield { type: 'done' }
}

function* quickSort(arr) {
  const a = arr.slice()
  const n = a.length

  function* quickSortRange(l, r) {
    if (l >= r) return
    const pivot = a[r]
    let storeIdx = l

    for (let i = l; i < r; i++) {
      yield { type: 'compare', indices: [i, r], line: 7 }
      if (a[i] < pivot) {
        if (i !== storeIdx) {
          ;[a[i], a[storeIdx]] = [a[storeIdx], a[i]]
          yield { type: 'swap', indices: [i, storeIdx], line: 9 }
        }
        storeIdx++
      }
    }

    if (storeIdx !== r) {
      ;[a[storeIdx], a[r]] = [a[r], a[storeIdx]]
      yield { type: 'swap', indices: [storeIdx, r], line: 16 }
    }

    yield { type: 'sorted', index: storeIdx, line: 18 }
    yield* quickSortRange(l, storeIdx - 1)
    yield* quickSortRange(storeIdx + 1, r)
  }

  yield* quickSortRange(0, n - 1)
  for (let i = 0; i < n; i++) {
    yield { type: 'sorted', index: i, line: 30 }
  }
  yield { type: 'done' }
}

export const ALGORITHMS = [
  { key: 'bubble', label: 'Bubble Sort', fn: bubbleSort, code: BUBBLE_CODE },
  { key: 'selection', label: 'Selection Sort', fn: selectionSort, code: SELECTION_CODE },
  { key: 'insertion', label: 'Insertion Sort', fn: insertionSort, code: INSERTION_CODE },
  { key: 'merge', label: 'Merge Sort', fn: mergeSort, code: MERGE_CODE },
  { key: 'quick', label: 'Quick Sort', fn: quickSort, code: QUICK_CODE },
]
